import { z } from 'zod'
import {
  PropertyType,
  PropertyUse,
  StateAbbreviation,
} from './generated/graphql-types'

export enum HelocPartner {
  Figure = 'figure',
  NftyDoor = 'nftydoor',
  SpringEq = 'springeq',
}

export const helocPartnerSchema = z.nativeEnum(HelocPartner)

const PARTNER_FILE_NAME_PREFIXES: ReadonlyArray<[RegExp, HelocPartner]> = [
  [/^figure_/, HelocPartner.Figure],
  [/^nftydoor/, HelocPartner.NftyDoor],
  [/^spring eq/, HelocPartner.SpringEq],
]

/**
 * Maps a vectorized guideline file name to its partner until a partner column exists on
 * `loan_assistant_document_vectors`. Case-insensitive; collapses repeated whitespace because
 * the real names are inconsistently spaced.
 */
export function partnerFromFileName(fileName: string): HelocPartner | null {
  const normalized = fileName.trim().replace(/\s+/g, ' ').toLowerCase()
  const match = PARTNER_FILE_NAME_PREFIXES.find(([pattern]) =>
    pattern.test(normalized)
  )
  return match ? match[1] : null
}

// Process documents (app stages, status tables, decline codes) must not become rules.
export const HelocGuidelineDocumentRole = {
  Eligibility: 'eligibility',
  Process: 'process',
} as const
export type HelocGuidelineDocumentRole =
  (typeof HelocGuidelineDocumentRole)[keyof typeof HelocGuidelineDocumentRole]

// Unlisted partner documents default to eligibility so a new PDF is read, never silently skipped.
const PROCESS_DOCUMENT_PATTERNS: ReadonlyArray<RegExp> = [
  /^figure_decline_reasons/,
  /^figure_heloc_app_stages/,
  /^figure_heloc_status_tables/,
]

export function documentRoleFromFileName(
  fileName: string
): HelocGuidelineDocumentRole | null {
  if (partnerFromFileName(fileName) === null) return null
  const normalized = fileName.trim().replace(/\s+/g, ' ').toLowerCase()
  return PROCESS_DOCUMENT_PATTERNS.some((pattern) => pattern.test(normalized))
    ? HelocGuidelineDocumentRole.Process
    : HelocGuidelineDocumentRole.Eligibility
}

export const RULE_SET_SCHEMA_VERSION = 1

export const OwnershipType = {
  Individual: 'individual',
  RevocableTrust: 'revocable_trust',
  IrrevocableTrust: 'irrevocable_trust',
  Llc: 'llc',
  Other: 'other',
} as const
export type OwnershipType = (typeof OwnershipType)[keyof typeof OwnershipType]

/**
 * The closed list of inputs the engine knows how to read. Each entry names its source on
 * `ApplicationRevision`, "derived" (computed by the snapshot builder), or "not collected yet"
 * (stays in the union so rules can name it; the engine returns `incomplete`).
 * Money is in dollars. Ratios (`loan.cltv`, `borrower.dti`) are decimals: 0.85 means 85%.
 */
export const SNAPSHOT_FIELDS = [
  // ApplicationRevision.state (property_state is never populated on HomeEquity revisions)
  'property.state',
  // ApplicationRevision.property_type; PropertyType values. NotSpecified is not a value a
  // rule may name; the snapshot builder maps it to null (unknown).
  'property.type',
  // ApplicationRevision.property_use; PropertyUse values, same NotSpecified handling
  'property.occupancy',
  // ApplicationRevision.property_value
  'property.value',
  // ApplicationRevision.remaining_balance
  'property.existing_first_lien_balance',
  // not collected yet (expense_second_mortgage is a payment, not a balance)
  'property.existing_second_lien_balance',
  // not collected yet
  'property.listed_for_sale',
  // not collected yet
  'property.acreage',
  // not collected yet; OwnershipType values
  'property.ownership_type',
  // not collected yet; days since the county-recorded transfer
  'property.seasoning_days',
  // ApplicationRevision.credit_score
  'borrower.credit_score',
  // ApplicationRevision.debt_to_income, stored as a percent (35) and empty on HomeEquity
  // revisions; the snapshot builder divides by 100 so rules compare decimals
  'borrower.dti',
  // not collected yet
  'borrower.financed_property_count',
  // ApplicationRevision.cash_out (HELOC loan_amount mirrors it)
  'loan.requested_amount',
  // derived: (existing lien balances + requested amount) / property value; decimal ratio
  'loan.cltv',
  // derived: 1 + number of existing liens
  'loan.lien_position',
] as const

export type SnapshotField = (typeof SNAPSHOT_FIELDS)[number]

export const snapshotFieldSchema = z.enum(SNAPSHOT_FIELDS)

type FieldType =
  | { kind: 'number' }
  | { kind: 'ratio' }
  | { kind: 'boolean' }
  | { kind: 'enum'; values: readonly string[] }

const RATIO_MAX = 2

const NOT_SPECIFIED = 'NotSpecified'

const SNAPSHOT_FIELD_TYPES: Record<SnapshotField, FieldType> = {
  'property.state': { kind: 'enum', values: Object.values(StateAbbreviation) },
  'property.type': {
    kind: 'enum',
    values: Object.values(PropertyType).filter(
      (value) => value !== NOT_SPECIFIED
    ),
  },
  'property.occupancy': {
    kind: 'enum',
    values: Object.values(PropertyUse).filter(
      (value) => value !== NOT_SPECIFIED
    ),
  },
  'property.value': { kind: 'number' },
  'property.existing_first_lien_balance': { kind: 'number' },
  'property.existing_second_lien_balance': { kind: 'number' },
  'property.listed_for_sale': { kind: 'boolean' },
  'property.acreage': { kind: 'number' },
  'property.ownership_type': {
    kind: 'enum',
    values: Object.values(OwnershipType),
  },
  'property.seasoning_days': { kind: 'number' },
  'borrower.credit_score': { kind: 'number' },
  'borrower.dti': { kind: 'ratio' },
  'borrower.financed_property_count': { kind: 'number' },
  'loan.requested_amount': { kind: 'number' },
  'loan.cltv': { kind: 'ratio' },
  'loan.lien_position': { kind: 'number' },
}

/** Allowed values for the enum-typed snapshot fields; rules must use these exact strings. */
export const SNAPSHOT_FIELD_VALUES: Partial<
  Record<SnapshotField, readonly string[]>
> = Object.fromEntries(
  Object.entries(SNAPSHOT_FIELD_TYPES).flatMap(([field, type]) =>
    type.kind === 'enum' ? [[field, type.values]] : []
  )
)

const scalarSchema = z.union([z.number(), z.string(), z.boolean()])
const listItemSchema = z.union([z.number(), z.string()])

// Every object is strict: an unknown key (a misspelled `applies_when`, say) fails validation
// instead of being stripped and silently changing what the rule means.
const conditionShapeSchema = z.discriminatedUnion('op', [
  z
    .object({
      field: snapshotFieldSchema,
      op: z.enum(['eq', 'neq']),
      value: scalarSchema,
    })
    .strict(),
  z
    .object({
      field: snapshotFieldSchema,
      op: z.enum(['lt', 'lte', 'gt', 'gte']),
      value: z.number(),
    })
    .strict(),
  z
    .object({
      field: snapshotFieldSchema,
      op: z.enum(['in', 'not_in']),
      value: z.array(listItemSchema).min(1),
    })
    .strict(),
  z
    .object({
      field: snapshotFieldSchema,
      op: z.literal('between'),
      value: z
        .tuple([z.number(), z.number()])
        .refine(([min, max]) => min <= max, 'between needs [min, max]'),
    })
    .strict(),
])

function valueMatchesField(
  type: FieldType,
  value: number | string | boolean
): boolean {
  switch (type.kind) {
    case 'number':
      return typeof value === 'number'
    case 'ratio':
      return typeof value === 'number' && value > 0 && value <= RATIO_MAX
    case 'boolean':
      return typeof value === 'boolean'
    case 'enum':
      return typeof value === 'string' && type.values.includes(value)
  }
}

/**
 * Operator semantics on a snapshot value `actual`: `between` is inclusive on both ends;
 * `in`/`not_in` test membership; the rest are the obvious comparisons. A null or missing
 * `actual` is never compared. In `check` it makes the rule `incomplete`; in `applies_when` it
 * makes the rule `skipped` with the field reported in `missing_fields`, and the engine must
 * treat that as an incomplete evaluation, never as "the rule does not apply".
 */
export const ruleConditionSchema = conditionShapeSchema.superRefine(
  (condition, ctx) => {
    const type = SNAPSHOT_FIELD_TYPES[condition.field]
    const raw: unknown = condition.value
    const values = (Array.isArray(raw) ? raw : [raw]) as (
      | number
      | string
      | boolean
    )[]
    const ordered = ['lt', 'lte', 'gt', 'gte', 'between'].includes(condition.op)
    if (ordered && type.kind !== 'number' && type.kind !== 'ratio') {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['op'],
        message: `${condition.field} is not numeric; use eq, neq, in or not_in`,
      })
      return
    }
    if (type.kind === 'boolean' && !['eq', 'neq'].includes(condition.op)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['op'],
        message: `${condition.field} is boolean; use eq or neq`,
      })
      return
    }
    const invalid = values.filter((value) => !valueMatchesField(type, value))
    if (invalid.length === 0) return
    const expected =
      type.kind === 'enum'
        ? `one of ${type.values.join(', ')}`
        : type.kind === 'ratio'
          ? `a decimal ratio in (0, ${RATIO_MAX}] (write 0.85 for 85%)`
          : `a ${type.kind}`
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['value'],
      message: `${condition.field} expects ${expected}; got ${JSON.stringify(invalid)}`,
    })
  }
)

export type RuleCondition = z.infer<typeof ruleConditionSchema>
export type RuleOperator = RuleCondition['op']

export const ruleSeveritySchema = z.enum(['blocking', 'warning'])
export type RuleSeverity = z.infer<typeof ruleSeveritySchema>

const ruleSourceSchema = z
  .object({
    file_name: z.string().trim().min(1),
    page: z.number().int().positive(),
    quote: z.string().optional(),
  })
  .strict()
export type RuleSource = z.infer<typeof ruleSourceSchema>

const ruleBaseShape = {
  id: z.string().regex(/^[a-z0-9]+(_[a-z0-9]+)*$/, 'snake_case slug'),
  description: z.string().trim().min(1),
  severity: ruleSeveritySchema,
  applies_when: z.array(ruleConditionSchema).optional(),
  source: ruleSourceSchema,
}

const checkRuleSchema = z
  .object({
    ...ruleBaseShape,
    kind: z.literal('check'),
    check: ruleConditionSchema,
  })
  .strict()

const matrixRowSchema = z
  .object({
    label: z.string().optional(),
    when: z.array(ruleConditionSchema).min(1),
    limits: z.array(ruleConditionSchema).min(1),
  })
  .strict()

/**
 * A tier table whose rows overlap (Figure's eligibility matrices, NFTYDoor's credit matrix).
 * Passes when at least one row has every `when` and every `limits` condition true. Fails
 * when every row is conclusively ruled out. Otherwise `incomplete`.
 */
const matrixRuleSchema = z
  .object({
    ...ruleBaseShape,
    kind: z.literal('matrix'),
    rows: z.array(matrixRowSchema).min(1),
  })
  .strict()

export const ruleSchema = z.discriminatedUnion('kind', [
  checkRuleSchema,
  matrixRuleSchema,
])

export type Rule = z.infer<typeof ruleSchema>
export type CheckRule = z.infer<typeof checkRuleSchema>
export type MatrixRule = z.infer<typeof matrixRuleSchema>
export type MatrixRow = z.infer<typeof matrixRowSchema>

export const ambiguitySeveritySchema = z.enum(['blocking', 'material'])
export type AmbiguitySeverity = z.infer<typeof ambiguitySeveritySchema>

/**
 * A conflict or gap the generator found in the documents. The rule carries the conservative
 * reading; the reviewer approving the draft resolves the doubt here. The engine ignores it.
 */
export const ambiguitySchema = z
  .object({
    topic: z.string().trim().min(1),
    issue: z.string().trim().min(1),
    sources: z.array(ruleSourceSchema).min(1),
    severity: ambiguitySeveritySchema,
    resolution: z.string().trim().min(1).optional(),
  })
  .strict()
export type Ambiguity = z.infer<typeof ambiguitySchema>

/** The single validator for generator output, the save tool, and the engine. */
export const ruleSetSchema = z
  .object({
    schema_version: z.literal(RULE_SET_SCHEMA_VERSION),
    partner: helocPartnerSchema,
    summary: z.string().trim().min(1),
    // lower = preferred when several partners are eligible
    priority: z.number().int().nonnegative(),
    // product or channel choices baked into the rules, e.g. "retail channel", "fixed-rate HELOC"
    assumptions: z.array(z.string()).optional(),
    rules: z.array(ruleSchema).min(1),
    ambiguities: z.array(ambiguitySchema).optional(),
    derived_fields_note: z.string().optional(),
  })
  .strict()
  .superRefine((ruleSet, ctx) => {
    const seen = new Set<string>()
    ruleSet.rules.forEach((rule, index) => {
      if (seen.has(rule.id)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['rules', index, 'id'],
          message: `duplicate rule id "${rule.id}"`,
        })
      }
      seen.add(rule.id)
    })
  })

export type RuleSet = z.infer<typeof ruleSetSchema>
